import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './App.css'
import './App.mobile.css'
import App from './App.tsx'
import { AppErrorBoundary } from './components/AppErrorBoundary.tsx'

const isIOSStandalone =
  window.matchMedia('(display-mode: standalone)').matches ||
  // @ts-expect-error legacy iOS PWA flag
  window.navigator.standalone === true

if (isIOSStandalone) {
  document.body.classList.add('ios-standalone')
}

// Recover from stale lazy-chunk references after a new deploy: when the PWA
// service worker has cached an old index.html but the new chunks live under
// new hashes, dynamic import() throws and the user gets stuck on a frozen
// route. We unregister stale service workers, drop their caches, and reload
// from network exactly once per session so the new manifest takes over.
const CHUNK_RECOVERY_KEY = 'bbg-chunk-recovery-at'
async function recoverFromStaleBundle(): Promise<void> {
  try {
    const regs = await navigator.serviceWorker?.getRegistrations?.()
    if (regs) await Promise.all(regs.map((reg) => reg.unregister()))
  } catch {
    /* best-effort */
  }
  try {
    const keys = await caches?.keys?.()
    if (keys) await Promise.all(keys.map((key) => caches.delete(key)))
  } catch {
    /* best-effort */
  }
  // Bypass HTTP cache by adding a unique query to the URL on reload
  const url = new URL(window.location.href)
  url.searchParams.set('_bbg_recover', String(Date.now()))
  window.location.replace(url.toString())
}
function handleChunkLoadFailure(error: unknown): boolean {
  const message = String((error as { message?: string })?.message || error || '')
  const name = String((error as { name?: string })?.name || '')
  const isChunkError =
    /Failed to fetch dynamically imported module|Loading chunk \d+ failed|ChunkLoadError|Importing a module script failed|Unexpected token '<'|MIME type.+not executable|Failed to load module script/i.test(
      message,
    ) ||
    name === 'ChunkLoadError'
  if (!isChunkError) return false
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RECOVERY_KEY) || '0')
    // Hard guard: 60s cooldown so a stuck SW cannot trigger an infinite recovery loop
    if (Date.now() - last < 60_000) return false
    sessionStorage.setItem(CHUNK_RECOVERY_KEY, String(Date.now()))
  } catch {
    /* sessionStorage unavailable — still attempt recovery */
  }
  void recoverFromStaleBundle()
  return true
}
window.addEventListener('error', (event) => {
  handleChunkLoadFailure(event.error || event.message)
})
window.addEventListener('unhandledrejection', (event) => {
  handleChunkLoadFailure(event.reason)
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </StrictMode>,
)
